import { Trans, getI18n } from 'react-i18next';
import i18n from 'locales/i18n';
import {
	BoldVerticalBarChartData,
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import { LineChartProps } from '@components/report/chart/line-chart/LineChart';
import { RadialBarChartProps } from '@components/report/chart/radial-bar-chart/RadialBarChart';

import { TriangleChartProps } from '@components/report/chart/triangle-chart/TriangleChart';
import { formatBoldVerticalChartData } from '@components/report/chart/utils';
import { VerticalBarAverageChartProps } from '@components/report/chart/vertical-bar-average-chart/VerticalBarAverageChart';

import { IGReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { FollowerGrowthIgProps } from './FollowerGrowth';

export class ActiveFollowersRateAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get realFollowerRate() {
		return this.value?.follow_stats?.real_follower_rate ?? 0;
	}
	get realFollower() {
		return this.value?.follow_stats?.real_follower ?? undefined;
	}
	get follower() {
		return this.value?.follow_stats?.follower ?? undefined;
	}

	adapt(): RadialBarChartProps {
		return {
			value: roundFloat(this.realFollowerRate * 100, 'down', 2) ?? undefined,
			legends: [
				{
					color: 'primary',
					label:
						// 예상 유효 팔로워 수
						i18n?.t('infReportInstagram:follower-growth.real-follower.label') ?? '',
					value: this.realFollower
						? i18n?.t('unit.number-of-person', {
								number: round(this.realFollower, 'down'),
						  })
						: undefined,
					isMain: true,
				},
				{
					color: 'gray',
					label:
						// 팔로워 수
						i18n?.t('infReportInstagram:follower-growth.follower.label') ?? '',
					value: this.follower
						? i18n?.t('unit.number-of-person', { number: round(this.follower, 'down') })
						: undefined,
				},
			],
			isPositive: true,
		};
	}
}

export class ActiveFollowersComparisonAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	/**
	 * @description 예상 유효 팔로워 수
	 */
	get realFollower() {
		return this.value?.follow_stats?.real_follower ?? undefined;
	}
	/**
	 * @description 유사 그룹 평균 예상 유효 팔로워 수
	 */
	get avgRealFollower() {
		return this.value?.follow_stats?.avg_similar_group_real_follower ?? undefined;
	}

	/**
	 * @description 비교 결과 텍스트
	 */
	textMapper = {
		high: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportInstagram:follower-growth.high-comparison'}
				components={[
					<span key="high">{i18n?.t('infReportInstagram:follower-growth.high')}</span>,
				]}
			/>
		),
		normal: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportInstagram:follower-growth.normal-comparison'}
				components={[
					<span key="high">
						{/* 비슷합니다. */}
						{i18n?.t('infReportInstagram:follower-growth.normal')}
					</span>,
				]}
			/>
		),
		low: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportInstagram:follower-growth.low-comparison'}
				components={[
					<span key="high">{i18n?.t('infReportInstagram:follower-growth.low')}</span>,
				]}
			/>
		),
		unmeasurable: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportInstagram:follower-growth.unmeasurable-comparison'}
				components={[
					<span key="unmeasurable">
						{i18n?.t('infReportInstagram:follower-growth.unmeasurable')}
					</span>,
				]}
			/>
		),
	} as const;

	/**
	 * @description 비교 결과 상태
	 */
	get status() {
		const hasAllValue = this.realFollower && this.avgRealFollower;
		if (!hasAllValue) return 'unmeasurable';

		return this?.realFollower > this.avgRealFollower * 1.1
			? 'high'
			: this?.realFollower < this.avgRealFollower * 0.9
			? 'low'
			: 'normal';
	}

	/**
	 * @description 인플루언서 유효 팔로워 차트데이터 (주 데이터)
	 */
	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realFollower && this.avgRealFollower;
		return {
			value: i18n?.t('unit.number-of-person', {
				number: round(this?.realFollower ?? 0, 'down'),
			}),
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.realFollower / (this.realFollower + this.avgRealFollower)) * 100
				: undefined,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		};
	}

	/**
	 * @description 유사 그룹 평균 유효 팔로워 차트데이터 (비교군 데이터)
	 */
	get similarGroupAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realFollower && this.avgRealFollower;
		return {
			value: i18n?.t('unit.number-of-person', {
				number: round(this?.avgRealFollower ?? 0, 'down'),
			}),
			label: i18n?.t('infReportInstagram:follower-growth.real-follower.similar-group'), //유사 그룹 평균
			valuePercentage: hasAllValue
				? (this.avgRealFollower / (this.realFollower + this.avgRealFollower)) * 100
				: undefined,
			colorType: 'gray',
		};
	}

	/**
	 * @description 차트데이터 전체
	 */
	get data(): BoldVerticalBarChartData[] {
		const realFollowerDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgRealFollowerDefaultData = {
			label: i18n?.t('infReportInstagram:follower-growth.real-follower.similar-group'),
		};

		const realFollowerData = formatBoldVerticalChartData(
			this.realFollower,
			realFollowerDefaultData,
			this.infAdvancedData,
		);
		const avgRealFollowerData = formatBoldVerticalChartData(
			this.avgRealFollower,
			avgRealFollowerDefaultData,
			this.similarGroupAdvancedData,
		);

		return [realFollowerData, avgRealFollowerData];
	}

	/**
	 * @description 유효 팔로워 수 텍스트
	 */
	get valueText(): string {
		if (!this.realFollower)
			return i18n?.t('infReportInstagram:follower-growth.real-follower.preparing') ?? '';

		return (
			i18n?.t('unit.number-of-person', { number: round(this.realFollower, 'down') }) ?? ''
		);
	}

	adapt(): BoldVerticalBarChartProps {
		return {
			desc: (
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportInstagram:follower-growth.real-follower.description'}
					components={[
						<span key={'real-follower'}>{this.valueText}</span>,
						this.textMapper[this.status],
					]}
				/>
			),
			data: this.data,
		};
	}
}

export class AverageReachAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get avgReach() {
		return this.value?.follow_stats?.avg_reach ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				// 예상 평균 도달 수는 <0/>입니다.
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportInstagram:follower-growth.avg-reach.description'}
					components={[
						<span key="avg-reach">
							{i18n?.t('unit.number-of-person', {
								number: this.avgReach ? round(this.avgReach, 'down') : '-',
							})}
						</span>,
					]}
				/>
			),
			barChartValues: [50, 30, 40, 70, 80, 50, 60],
			value: this.avgReach ?? undefined,
			isPositive: true,
		};
	}
}

export class FollowerTrendAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get followerList() {
		return (
			(this.value?.follow_stats?.follow_trend?.follower_list ?? []).map(
				(v) => Number(v) ?? 0,
			) ?? []
		);
	}
	get dateList() {
		return (
			(this.value?.follow_stats?.follow_trend?.date_list ?? []).map((v) => v ?? '') ?? []
		);
	}
	get difference() {
		return this.followerList[0] - this.followerList[this.followerList.length - 1];
	}

	get isPositive() {
		return this.difference < 0 ? 'high' : this.difference > 0 ? 'low' : 'normal';
	}

	textMapper = {
		high: i18n?.t('infReportInstagram:follower-growth.follower-trend.text.high'), //' 성장했습니다.',
		low: i18n?.t('infReportInstagram:follower-growth.follower-trend.text.low'), //' 하락했습니다.',
		normal: i18n?.t('infReportInstagram:follower-growth.follower-trend.text.normal'), //'변동이 없습니다.',
	};

	adapt(): LineChartProps {
		if (this.followerList.length === 0) return { isError: true };

		return {
			desc: (
				<Trans
					// t={i18n?.t}
					i18nKey={
						'infReportInstagram:follower-growth.follower-list-difference.description'
					}
					components={[
						<span key={'difference'}>
							{this.difference !== 0
								? i18n?.t('unit.number-of-person', {
										number: round(Math.abs(this.difference), 'down'),
								  })
								: ''}
							{this.textMapper[this.isPositive]}
						</span>,
					]}
				/>
			),
			datas_a: this.followerList,
			categorys: this.dateList,
			isPositive: this.isPositive === 'high',
		};
	}
}

export class FollowerGrowthRateAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get followerGrowthRate() {
		return this.value?.follow_stats?.follow_trend?.follower_growth_rate ?? undefined;
	}
	get followerGrowthRateInSimilarGroup() {
		return (
			this.value?.follow_stats?.follower_growth_rate_rank_in_similar_group ?? undefined
		);
	}

	adapt(): TriangleChartProps {
		if (!this.followerGrowthRate && !this.followerGrowthRateInSimilarGroup)
			return { isError: true };
		return {
			desc: (
				<>
					{/* 팔로워 성장률이 <0/>로 유사 그룹 중 <1/>에 해당됩니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportInstagram:follower-growth.follower-growth-rate.description'
						}
						components={[
							<span key={'follower-growth-rate'}>
								{this.followerGrowthRate?.toString()
									? roundFloat(this.followerGrowthRate * 100, 'down', 2)
									: '-'}
								%
							</span>,
							<span key={'percentage'}>
								{i18n?.t(
									'infReportInstagram:follower-growth.follower-growth-rate.span-text',
									{
										number:
											(this.followerGrowthRateInSimilarGroup
												? roundFloat(this.followerGrowthRateInSimilarGroup, 'down', 2)
												: '-') + '%',
									},
								)}
							</span>,
						]}
					/>
				</>
			),
			valuePercentage: this.followerGrowthRateInSimilarGroup ?? undefined,
		};
	}
}

export class FollowingTrendAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get followingList() {
		return (this.value?.follow_stats?.follow_trend?.following_list ?? []).map(
			(v) => Number(v) ?? 0,
		);
	}
	get dateList() {
		return (this.value?.follow_stats?.follow_trend?.date_list ?? []).map((v) => v ?? '');
	}
	get difference() {
		return this.followingList[0] - this.followingList[this.followingList.length - 1];
	}

	get isPositive() {
		return this.difference < 0 ? 'high' : this.difference > 0 ? 'low' : 'normal';
	}

	textMapper = {
		high: i18n?.t('infReportInstagram:follower-growth.follower-trend.text.high'), //' 성장했습니다.',
		low: i18n?.t('infReportInstagram:follower-growth.follower-trend.text.low'), //' 하락했습니다.',
		normal: i18n?.t('infReportInstagram:follower-growth.follower-trend.text.normal'), //'변동이 없습니다.',
	};

	adapt(): LineChartProps {
		if (this.followingList.length === 0) return { isError: true };
		return {
			desc: (
				<>
					{/* 팔로잉이{' '}
					<span>
						{this.difference !== 0 ? `${round(Math.abs(this.difference), 'down')} 명` : ''}
						{this.textMapper[this.isPositive]}
					</span> */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportInstagram:follower-growth.following-list-difference.description'
						}
						components={[
							<span key={'difference'}>
								{this.difference !== 0
									? i18n?.t('unit.number-of-person', {
											number: round(Math.abs(this.difference), 'down'),
									  })
									: ''}
								{this.textMapper[this.isPositive]}
							</span>,
						]}
					/>
				</>
			),
			datas_a: this.followingList,
			categorys: this.dateList,
			isPositive: this.isPositive === 'high',
		};
	}
}

export class FollowerGrowthIgAdapter {
	value: IGReportRes;
	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	adapt(): FollowerGrowthIgProps {
		return {
			activeFollowersRate: new ActiveFollowersRateAdapter(this.value).adapt(),
			activeFollowersComparison: new ActiveFollowersComparisonAdapter(this.value).adapt(),
			averageReach: new AverageReachAdapter(this.value).adapt(),
			followerTrend: new FollowerTrendAdapter(this.value).adapt(),
			followerGrowthRate: new FollowerGrowthRateAdapter(this.value).adapt(),
			followingTrend: new FollowingTrendAdapter(this.value).adapt(),
		};
	}
}
